import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "../styles/MyTeam.css";

const normalizeCountryToFlagName = (country) =>
  String(country || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ /g, "_")
    .toLowerCase();

const MyTeam = ({
  generalCyclists,
  espoirCyclists,
  giroCyclists,
  tdfCyclists,
  vueltaCyclists,
}) => {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState("value"); // value | points
  const [teamKey, setTeamKey] = useState("general"); // general | espoir
  const [currentTeam, setCurrentTeam] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (teamKey === "espoir") {
      setCurrentTeam(espoirCyclists || []);
    } else if (teamKey === "giro") {
      setCurrentTeam(giroCyclists || []);
    } else if (teamKey === "tdf") {
      setCurrentTeam(tdfCyclists || []);
    } else if (teamKey === "vuelta") {
      setCurrentTeam(vueltaCyclists || []);
    } else {
      setCurrentTeam(generalCyclists || []);
    }
  }, [teamKey, generalCyclists, espoirCyclists, giroCyclists, tdfCyclists, vueltaCyclists]);

  const handleRowClick = (cyclistId) => {
    navigate(`/cyclistDetails/${cyclistId}`);
  };

  const sortedCyclists = useMemo(() => {
    const arr = Array.isArray(currentTeam) ? [...currentTeam] : [];

    return arr.sort((a, b) => {
      const aVal = Number(a?.finalValue ?? 0);
      const bVal = Number(b?.finalValue ?? 0);
      const aPts = Number(a?.cyclistPoints ?? 0);
      const bPts = Number(b?.cyclistPoints ?? 0);

      if (sortKey === "points") return bPts - aPts;
      return bVal - aVal; // default: value
    });
  }, [currentTeam, sortKey]);

  return (
    <section className="my-team">
      <div className="my-team-sort-control">
        <h2>{t("myTeam.title")}</h2>

        <label>{t("myTeam.sortBy")}</label>
        <select value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
          <option value="value">{t("myTeam.sort.value")}</option>
          <option value="points">{t("myTeam.sort.points")}</option>
        </select>

        <label>{t("myTeam.chooseTeam")}</label>
        <select value={teamKey} onChange={(e) => setTeamKey(e.target.value)}>
          <option value="general">{t("myTeam.team.general")}</option>
          <option value="espoir">{t("myTeam.team.espoir")}</option>
          <option value="giro">{t("myTeam.team.giro", { defaultValue: "Giro" })}</option>
          <option value="tdf">
            {t("myTeam.team.tdf", { defaultValue: "Tour de France" })}
          </option>
          <option value="vuelta">
            {t("myTeam.team.vuelta", { defaultValue: "Vuelta" })}
          </option>
        </select>
      </div>

      {sortedCyclists.length > 0 ? (
        <div className="my-team-table">
          <div className="my-team-table-container">
            <table>
              <thead>
                <tr>
                  <th>{t("myTeam.table.cyclist")}</th>
                  <th>{t("myTeam.table.team")}</th>
                  <th>{t("myTeam.table.value")}</th>
                  <th>{t("myTeam.table.speciality")}</th>
                  <th>{t("myTeam.table.points")}</th>
                </tr>
              </thead>

              <tbody>
                {sortedCyclists.map((cyclist) => (
                  <tr
                    key={cyclist.cyclistId}
                    onClick={() => handleRowClick(cyclist.cyclistId)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <div className="my-team-name-info">
                        {cyclist.isBonus === 1 && <p>{t("myTeam.bonusX2")}</p>}

                        <img
                          className="select-cyclists-flag-icon"
                          src={`${process.env.PUBLIC_URL}/flags/${normalizeCountryToFlagName(
                            cyclist.nationality
                          )}.png`}
                          alt={t("myTeam.flagAlt", {
                            country: cyclist.nationality,
                          })}
                          width="20"
                          height="20"
                        />

                        <span>{cyclist.name}</span>
                      </div>
                    </td>

                    <td>{cyclist.team}</td>
                    <td>{cyclist.finalValue}</td>
                    <td>{cyclist.speciality}</td>
                    <td>{cyclist.cyclistPoints}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p>{t("myTeam.empty")}</p>
      )}
    </section>
  );
};

export default MyTeam;
